import { ComponentProps, useContext, useState } from "react";

import { IDynamicFormItemProps } from ".";

import Button from "components/Button/Button";
import { TextLink } from "components/Buttons";
import { TextType } from "components/Text/Text";
import { ButtonFormItem } from "config";
import { AppError } from "config/errors";
import { useModal, useTranslation } from "modules";

import c from "classnames";
import { defaultTo } from "lodash-es";

import { DynamicFormContext } from "../DynamicFormContext";
import { EXPECTED_ACTION_ERROR, useButtonAction } from "../useButtonAction";

interface TButtonItem extends IDynamicFormItemProps<"button"> {
  buttonTextByStyle?: Partial<Record<DynamicFormItemButtonStyle, string>>;
  icon?: string;
  tag?: TextType;
}

/**
 * Button component for DynamicForm.Item.
 *  * A DynamicForm.Item button component
 * @property {Partial<Record<DynamicFormItemButtonStyle, string>>} buttonTextByStyle - Optional mapping of button styles to text overrides.
 * @property {ClassName} className - ClassNames to apply to the button.
 * @property {string} icon - Icon src to render on the button.
 * @property {TButtonItem} item - The button configuration, containing key, actions, primary status, style, and text.
 * @property {string} item.key - reserved ButtonItem.key = 'primary-button' functions as "submit" form button
 * @property {IButtonAction[]} [item.actions] - Actions to be executed when the button is clicked specified by order in the array.
 * @property {boolean} [item.primary] - Determines if the button is primary and should be disabled if required inputs are incomplete.
 * @property {DynamicFormItemButtonStyle} item.style - Corresponds to a subset of buttons with distinctive UI
 * @property {string} item.text - The text displayed on the button.
 * @property {TextType} tag - The Text tag type for text-based buttons.
 *
 * @returns A React element representing a button, either as a standard button or a text link.
 */
export function ButtonItem({ buttonTextByStyle = {}, className, icon, item, tag }: TButtonItem) {
  const isPrimaryButton = item?.key === "primary-button";
  const { actions, primary, style, text } = item?.content || {};

  // HOOKS
  const { t } = useTranslation();
  const { form } = useContext(DynamicFormContext);
  const actionCommands = useButtonAction(actions);
  const openModal = useModal();
  const [isDisabled, setIsDisabled] = useState(false);

  // LOCAL DERIVED VARIABLES
  const classes = c("dynamic-form-item dynamic-form-item--button", className, {
    "appear-hidden": style === "hidden",
  });
  const buttonComponentType = buttonStyleTypeMapper[style];
  const buttonIcon = icon || buttonStyleIconMapper[style];
  const isPrimaryButtonDisabled =
    primary && (!form.allRequiredFieldsFilled || form.loading || !form.valid);
  const buttonTextMap: Record<DynamicFormItemButtonStyle, string> = {
    back: t("back", "BACK").toLowerCase(),
    destructive: text,
    exit: t("exit", "EXIT").toLowerCase(),
    hidden: text,
    primary: text,
    secondary: text,
    textLink: text,
    ...buttonTextByStyle,
  };
  const buttonText = buttonTextMap[style];
  // LOCAL METHODS
  const handleOnClick = async () => {
    try {
      setIsDisabled(true);
      form.setLoading(true);
      form.setFormErrorMessage("");

      await actionCommands({ setButtonItemIsDisabled: setIsDisabled });

      if (isPrimaryButton && form.onSubmit) {
        await form.onSubmit(form);
      }
    } catch (err) {
      const appError = AppError.from(err);
      const appErrorMessage = defaultTo(appError.fullMessage, appError.code);

      // Process server validation errors on primary button click
      // and show error message if the button is primary
      if (primary || isPrimaryButton) {
        form.processServerValidationErrors(appError);
      }

      // If a generic error occurs, set the form error message
      if (primary && appErrorMessage !== EXPECTED_ACTION_ERROR) {
        form.setFormErrorMessage(
          appErrorMessage || t("errors.graphql_error", "Oops. There was an error.")
        );
      }

      // Else display a generic error modal
      // WIP - to be refactored with form error handling
      if (appError.message !== EXPECTED_ACTION_ERROR) {
        openModal("GeneralErrorModal", { error: appError.toPlainObject() });
      }
    } finally {
      setIsDisabled(false);
      form.setLoading(false);
    }
  };

  // RENDER
  if (style === "textLink") {
    return (
      <TextLink
        className={classes}
        isDisabled={isDisabled || isPrimaryButtonDisabled}
        onClick={handleOnClick}
        size="md"
        tag={tag}
        text={text}
      />
    );
  }

  return (
    <Button
      align="left"
      className={classes}
      dataTestId={`dynamic-form-item-button__${item.key}`}
      disabled={isDisabled || isPrimaryButtonDisabled}
      icon={buttonIcon}
      isLoading={isPrimaryButton && form.loading}
      onClick={handleOnClick}
      subtype={buttonComponentType}
      tag={tag}
      text={buttonText}
      type={isPrimaryButton ? "submit" : "button"}
    />
  );
}

/* ------------------    TYPES    ------------------ */
type DynamicFormItemButtonStyle = ButtonFormItem["content"]["style"];
type ButtonComponentTypes = ComponentProps<typeof Button>["subtype"];

const buttonStyleTypeMapper: Record<DynamicFormItemButtonStyle, ButtonComponentTypes> = {
  back: "dark",
  destructive: "text-error-dark",
  exit: "dark",
  hidden: "text-dark",
  primary: "primary",
  secondary: "text-dark-border",
  textLink: "text-dark",
};

const buttonStyleIconMapper: Record<DynamicFormItemButtonStyle, string> = {
  back: "BackCarat",
  destructive: "",
  exit: "Exit",
  hidden: "",
  primary: "",
  secondary: "",
  textLink: "",
};
